/* eslint-disable react/prop-types */
import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftSelect from "components/SoftSelect";

// example components
import { Stack } from "@mui/material";
import { Link } from "react-router-dom";
import SoftButton from "components/SoftButton";
import TableCommon from "components/TableCommon";

const years = Array.from({ length: 10 }, (_, i) => ({
  value: String(new Date().getFullYear() - i),
  label: String(new Date().getFullYear() - i),
}));

function DataTable({
  table,
  pagination,
  isSorted,
  noEndBorder,
  setQuery,
  query,
  paginationData,
  setPaginationData,
  loading,
  filters,
  setFilters,
  onSync,
  syncing,
  accounts,
  accountsLoading,
  onFetchData,
}) {
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    setPageSize,
    setGlobalFilter,
    state: { globalFilter },
  } = tableInstance;

  useEffect(() => setPageSize(paginationData.size), []);

  const [search, setSearch] = useState(globalFilter);

  const onSearchChange = useAsyncDebounce((value) => {
    setFilters((prev) => ({ ...prev, searchBy: value }));
  }, 100);

  const handleFetch = () => {
    onFetchData(filters);
  };

  const handleReset = () => {
    const resetFilters = {
      year: String(new Date().getFullYear()),
      fromDate: "",
      toDate: "",
      accountCode: accounts.length > 0 ? accounts[0].accountCode : "",
      searchBy: "",
    };
    setFilters(resetFilters);
    setSearch("");
    onFetchData(resetFilters);
  };

  const btnStyle = {
    height: "39px",
    borderRadius: 0,
    textTransform: "none",
    fontWeight: "bold",
  };

  return (
    <>
      <SoftBox
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        alignItems="flex-start"
        p={3}
        pb={1}
      >
        <SoftBox lineHeight={1} width="100%">
          <Stack spacing={1} direction="row" alignItems="flex-end" flexWrap="wrap">
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Năm
              </SoftTypography>
              <SoftBox width="8rem">
                <SoftSelect
                  value={years.find((y) => y.value === filters.year)}
                  options={years}
                  onChange={(opt) => setFilters((prev) => ({ ...prev, year: opt.value }))}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Từ ngày
              </SoftTypography>
              <SoftBox width="10rem">
                <SoftInput
                  type="date"
                  value={filters.fromDate}
                  onChange={({ currentTarget }) =>
                    setFilters((prev) => ({ ...prev, fromDate: currentTarget.value }))
                  }
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Đến ngày
              </SoftTypography>
              <SoftBox width="10rem">
                <SoftInput
                  type="date"
                  value={filters.toDate}
                  onChange={({ currentTarget }) =>
                    setFilters((prev) => ({ ...prev, toDate: currentTarget.value }))
                  }
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tài khoản ngân hàng
              </SoftTypography>
              <SoftBox width="12rem">
                <SoftSelect
                  placeholder="Chọn tài khoản..."
                  loading={accountsLoading}
                  value={accounts.find((a) => a.accountCode === filters.accountCode)}
                  options={accounts.map((a) => ({
                    value: a.accountCode,
                    label: `${a.accountCode} - ${a.accountName}`,
                  }))}
                  onChange={(opt) => setFilters((prev) => ({ ...prev, accountCode: opt.value }))}
                />
              </SoftBox>
            </SoftBox>

            <SoftBox display="flex" flexDirection="column">
              <SoftTypography component="label" variant="caption" fontWeight="bold" mb={0.5}>
                Tìm kiếm
              </SoftTypography>
              <SoftBox width="12rem">
                <SoftInput
                  placeholder="Search..."
                  value={search}
                  onChange={({ currentTarget }) => {
                    setSearch(currentTarget.value);
                    onSearchChange(currentTarget.value);
                  }}
                />
              </SoftBox>
            </SoftBox>

            <SoftButton
              variant="contained"
              style={{ ...btnStyle, backgroundColor: "#1a73e8", color: "white" }}
              onClick={handleFetch}
              disabled={loading}
            >
              Tải dữ liệu
            </SoftButton>

            <SoftButton
              variant="outlined"
              style={{
                ...btnStyle,
                backgroundColor: "white",
                color: "#344767",
                border: "1px solid #d2d6da",
              }}
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </SoftButton>

            {onSync && (
              <SoftButton
                variant="contained"
                style={{ ...btnStyle, backgroundColor: "#4CAF50", color: "white" }}
                onClick={onSync}
                disabled={loading || syncing}
              >
                {syncing ? "Đang đồng bộ..." : "Đồng bộ dữ liệu"}
              </SoftButton>
            )}
          </Stack>
        </SoftBox>
      </SoftBox>

      <TableCommon
        loading={loading}
        query={query}
        setQuery={setQuery}
        paginationData={paginationData}
        setPaginationData={setPaginationData}
        table={table}
        isSorted={isSorted}
        noEndBorder={noEndBorder}
        pagination={pagination}
        tableInstance={tableInstance}
      />
    </>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  query: PropTypes.any,
  setQuery: PropTypes.any,
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
